import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';

import type { BaseQuery } from './baseQuery.types';

type PagedResult = components['schemas']['GetOrganizationsWithUsersEndpoint_GetOrganizationsWithUsersPagedResponse'];

export interface UseOrganizationsWithUsersReturn extends BaseQuery {
  organizations: PagedResult | null;
}

export function useOrganizationsWithUsers(
  page: number,
  pageSize: number,
  sortedBy: string | null = null,
  usersEmailFilter: string[] = [],
  organizationIdsFilter: number[] = [],
): UseOrganizationsWithUsersReturn {
  const { getAccessToken } = useAuth();

  const getOrganizationsWithUsers = async (
    page: number,
    pageSize: number,
    sortedBy: string | null = null,
    usersEmailFilter: string[] = [],
    organizationIdsFilter: number[] = [],
  ) => {
    const client = createApiClient(getAccessToken);
    const { data } = await client.POST('/api/organizations/with-users', {
      params: {
        query: {
          Page: page,
          PageSize: pageSize,
          ...(sortedBy ? { SortedBy: sortedBy } : {}),
        },
      },
      body: {
        organizationIdsFilter: organizationIdsFilter,
        usersEmailFilter: usersEmailFilter,
      },
    });

    if (!data) {
      throw new Error('No data returned from API');
    }

    return data as PagedResult;
  };

  const useOrganizationsWithUsersQuery = useQuery({
    queryKey: ['organizationsWithUsers', page, pageSize, sortedBy, usersEmailFilter, organizationIdsFilter],
    queryFn: () => getOrganizationsWithUsers(page, pageSize, sortedBy, usersEmailFilter, organizationIdsFilter),
    retry: false,
  });

  const { data, isLoading, error, refetch, isRefetching, isPending } = useOrganizationsWithUsersQuery;

  return {
    organizations: data ?? null,
    isLoading: isLoading || isPending,
    isRefetching,
    error: error as Error | null,
    refetch,
  };
}
